import { useState } from "react";

import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Note } from "@/Common/Models/Note";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import styles from "./LabActionDetailsPopup.module.scss";

interface ILabActionDetailsPopupProps {
    notes: Note[];
    noteTextValue: string;
    detailTextValue: string;
    referenceId: string;
    subReferenceId: string;
    onNoteTextChange: (newText: string) => void;
    onClose: () => void;
    isManualLabEntry?: boolean;
}

function LabActionDetailsPopup(props: ILabActionDetailsPopupProps): JSX.Element {
    const clickableTextPanelId: string = "labActionDetailClickableTextPanel";

    const { getText } = useTranslation();
    const [isClickableTextPanelVisible, setIsClickableTextPanelVisible] = useState<boolean>(false);
    const [detail, setDetail] = useState<string>(props.detailTextValue);

    const renderModalFooter = (
        referenceId: string,
        subReferenceId: string,
        noteTextValue: string,
        detailTextValue: string,
        onClose: () => void,
        getText: (key: string) => string
    ): JSX.Element => (
        <div className="p-footer text-right">
            <button
                className="btn btn-default push-right-5"
                onClick={() => onClose()}
            >
                {getText("CancelLabel")}
            </button>
            <button
                className="btn btn-primary"
                onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.Note_Save, {
                    ReferenceId: referenceId,
                    SubReferenceId: subReferenceId,
                    value: noteTextValue,
                    details: detailTextValue,
                    isManualLabEntry: props.isManualLabEntry
                })}
            >
                {getText("NoteSubmitLabel")}
            </button>
        </div>
    );

    const renderNotes = (
        notes: Note[],
        value: string,
        onNoteTextChange: (newText: string) => void,
        getText: (key: string) => string
    ): JSX.Element => {
        let notesRenderList: JSX.Element[] = [];
        const hasNotes = notes?.length > 0;

        if (hasNotes) {
            for (let i = notes.length - 1; i >= 0; i--) {
                notesRenderList.push(renderNoteLine(notes[i], i, getText));
            }
        }

        return (
            <div className="notes-dropdown-content">
                <h5 className="bold">{getText("NotesDLabel")}</h5>
                {hasNotes && (
                    <div className="scroll-limit">
                        <div className={styles.customWidth95}> {/* Prevent Horizontal Scroll Bar */}
                            {notesRenderList}
                        </div>
                    </div>
                )}
                <textarea
                    id={clickableTextPanelId}
                    className={`form-control ${styles.resizeNone}`}
                    value={value}
                    onChange={p => onNoteTextChange(p.target.value)}
                    rows={4}
                />
            </div>
        );
    };

    const renderNoteLine = (note: Note, index: number, getText: (key: string) => string): JSX.Element => {
        const person = cacheStore.GetCliniciansPersonsByContactId(note.ContactId);

        return (
            <div key={`${note.NoteId} ${index}`} className="notes-collection-item">
                <div className="memo-header">
                    <strong className="margin-xs-right">{person.NameWithPrefix}</strong>
                    <span>
                        {`${LuxonDateTimeParser.parse(note.NoteDate).toFormat("yyyy-MM-dd")} ${getText("AtLabel")} ${LuxonDateTimeParser.parse(note.NoteDate).toFormat("HH:mm:ss")}`}
                    </span>
                </div>
                <div className="note-container">
                    {JSXHelper.BreakStringIntoParagraphs(note.NoteContent)}
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={getText("ResultDetailsLabel")}
            footer={(
                renderModalFooter(
                    props.referenceId,
                    props.subReferenceId,
                    props.noteTextValue,
                    detail,
                    props.onClose,
                    getText
                )
            )}
            onClose={() => props.onClose()}
            overlayZIndex={99}
            disableOverlayClose
            roundTopCorner
            draggable
            showX
        >
            <div onClick={() => setIsClickableTextPanelVisible(false)}>
                <div className="notes-dropdown-content">
                    <h5 className="bold">{getText("DetailsLabel")}</h5>
                    <TextboxControl
                        className="form-control"
                        value={detail}
                        onChange={(newText: string) => setDetail(newText)}
                        maxLength={256}
                    />
                </div>
                <br />
                <div className="notes-modal-result">
                    {renderNotes(props.notes, props.noteTextValue, props.onNoteTextChange, getText)}
                </div>
                <FixedClickableTextPanel
                    cssClass="text-helper-container"
                    context="ResultNotes"
                    inputElementId={clickableTextPanelId}
                    onCloseEvent={() => setIsClickableTextPanelVisible(false)}
                    isLinkDisabled={false}
                    isClickableTextPanelOpen={isClickableTextPanelVisible}
                    onClickableTextPanelOpen={(): void => setIsClickableTextPanelVisible(true)}
                    onClickableTextPanelClose={(): void => setIsClickableTextPanelVisible(false)}
                    reduceHeight
                />
            </div>
        </Modal>
    );
}

export { LabActionDetailsPopup };
